import JwtService from "@/core/services/JwtService";

/** A write the chat proposed; it happens only when the user presses its button. */
export interface ChatPendingAction {
  id: string;
  etichetta: string;
  voci: Array<{ nome: string; data: string | null; sostituisce: boolean }>;
  status?: "idle" | "saving" | "done" | "error";
  result?: string;
}

export type SseEvent =
  | { type: "message_start"; user_message_id: number }
  | { type: "text_delta"; delta: string }
  | { type: "reasoning_delta"; delta: string }
  | ({ type: "pending_action" } & ChatPendingAction)
  | { type: "tool_call_start"; tool_call_id: string; tool: string; args: Record<string, unknown> }
  | { type: "tool_call_end"; tool_call_id: string; tool: string; ok: boolean; latency_ms: number }
  | {
      type: "done";
      assistant_message_id: number | null;
      conversation_id: number;
      title: string | null;
      usage: {
        input_tokens: number;
        cache_read_tokens: number;
        cache_write_tokens: number;
        output_tokens: number;
        cost_usd: number;
      };
    }
  | { type: "error"; message: string }
  | { type: "close" };

/**
 * Reads an SSE stream from the chat endpoint.
 * Uses native fetch() because axios doesn't support streaming bodies.
 */
export default class ChatStreamService {
  private controller: AbortController | null = null;

  abort(): void {
    if (this.controller) {
      this.controller.abort();
      this.controller = null;
    }
  }

  async stream(
    conversationId: number,
    message: string,
    onEvent: (event: SseEvent) => void
  ): Promise<void> {
    this.controller = new AbortController();
    const baseUrl = (process.env.VUE_APP_API_URL || "").replace(/\/$/, "");
    const url = `${baseUrl}/admin/chat/conversations/${conversationId}/stream`;
    const token = JwtService.getToken();

    let response: Response;
    try {
      response = await fetch(url, {
        method: "POST",
        signal: this.controller.signal,
        credentials: "include",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "text/event-stream",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({ message }),
      });
    } catch (e: unknown) {
      const err = e as { name?: string; message?: string };
      if (err.name === "AbortError") {
        onEvent({ type: "close" });
        return;
      }
      onEvent({ type: "error", message: err.message || "Network error" });
      return;
    }

    if (!response.ok || !response.body) {
      let detail = `HTTP ${response.status}`;
      try {
        const text = await response.text();
        detail += `: ${text.slice(0, 300)}`;
      } catch {
        // ignore
      }
      onEvent({ type: "error", message: detail });
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let buffer = "";

    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let nl: number;
        // SSE frames are separated by blank lines (\n\n). We parse by single newline
        // and treat each "data:" line as a self-contained JSON event (our server emits one event per data: line).
        while ((nl = buffer.indexOf("\n")) !== -1) {
          const line = buffer.slice(0, nl).trim();
          buffer = buffer.slice(nl + 1);
          if (line === "" || line.startsWith(":")) continue;
          if (!line.startsWith("data:")) continue;
          const dataStr = line.slice(5).trim();
          if (!dataStr) continue;
          try {
            const evt = JSON.parse(dataStr) as SseEvent;
            onEvent(evt);
          } catch (parseErr) {
            console.warn("SSE parse error", parseErr, dataStr);
          }
        }
      }
    } catch (e: unknown) {
      const err = e as { name?: string; message?: string };
      if (err.name !== "AbortError") {
        onEvent({ type: "error", message: err.message || "Stream error" });
      }
    } finally {
      this.controller = null;
      onEvent({ type: "close" });
    }
  }
}
